import { useEffect, useState } from 'react'
import './CopyableRegistration.css'

/**
 * The detail page's registration number, copied by clicking it.
 *
 * Distinct from the register's <CopyButton>, which is a small icon beside a
 * scannable column of 40 plates. Here there is exactly one number, it is the
 * largest thing on the page, and it is the thing people are about to paste into
 * VAHAN or an insurer's portal. Making the number itself the target is fewer
 * pixels to hit than a 20px icon, and it is where the hand already is.
 *
 * Copies the canonical spelling: MH12EQ3823, not "MH 12 EQ 3823", because the
 * spaced form fails those lookups.
 */
export function CopyableRegistration({ registration }: { registration: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const timer = setTimeout(() => setState('idle'), 1600)
    return () => clearTimeout(timer)
  }, [state])

  async function copy() {
    try {
      // Undefined outside a secure context. localhost counts, so this only bites
      // when served over plain HTTP — which the fallback covers.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(registration)
      } else if (!legacyCopy(registration)) {
        throw new Error('copy rejected')
      }

      setState('copied')
    } catch {
      // Surfaced, not swallowed: believing you have the number when you do not is
      // the failure that matters, because the next thing is a paste.
      setState('failed')
    }
  }

  return (
    <h1 className="detail__reg">
      <button type="button" className={`regcopy regcopy--${state}`} onClick={() => void copy()}>
        <span className="mono">{registration}</span>
        <span className="regcopy__hint" aria-hidden="true">
          {state === 'copied' ? 'copied' : state === 'failed' ? 'copy failed' : 'click to copy'}
        </span>
      </button>

      {/* The visual hint is aria-hidden because it is always present and would be
          read as part of the heading. This announces only the outcome. */}
      <span className="visually-hidden" aria-live="polite">
        {state === 'copied'
          ? `Copied ${registration}`
          : state === 'failed'
            ? 'Copy failed — select the number instead'
            : ''}
      </span>
    </h1>
  )
}

function legacyCopy(value: string): boolean {
  const area = document.createElement('textarea')
  area.value = value
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)

  try {
    area.select()
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(area)
  }
}
